import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { MarketingProduct } from "@/product-marketing/types";
import { PRODUCT_MARKETING } from "./content";

export const ProductExample = ({ product }: { product: MarketingProduct }) => {
  const content = PRODUCT_MARKETING[product];
  return (
    <figure
      aria-label={`Example ${content.name} preview`}
      className="w-full min-w-0"
    >
      <Card size="sm" className="gap-0 py-0">
        <CardHeader className="border-b border-line py-4">
          <CardTitle>{content.previewTitle}</CardTitle>
          <CardDescription>{content.previewLabel}</CardDescription>
        </CardHeader>
        <CardContent className="py-4">
          <dl className="grid grid-cols-3 gap-4">
            {content.metrics.map(({ label, value }) => (
              <Stack align="start" key={label} gap={1}>
                <dt>
                  <Text variant="caption" tone="muted">
                    {label}
                  </Text>
                </dt>
                <dd className="text-base leading-5 font-medium tabular-nums">
                  {value}
                </dd>
              </Stack>
            ))}
          </dl>
          <Table className="mt-4 tabular-nums">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {content.columns.map((column, index) => (
                  <TableHead
                    key={column}
                    scope="col"
                    className={cn({
                      "text-right": content.rows.every((row) =>
                        row[index]?.startsWith("$"),
                      ),
                    })}
                  >
                    {column}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {content.rows.map((row) => (
                <TableRow key={row[0]} className="hover:bg-transparent">
                  {row.map((cell, index) => (
                    <TableCell
                      key={`${row[0]}-${content.columns[index]}`}
                      className={cn({
                        "text-right": cell.startsWith("$"),
                        "font-medium":
                          index === row.length - 1 && cell.startsWith("$"),
                      })}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Text className="mt-4 text-pretty">
            <span className="font-medium">{content.previewCalloutTitle}. </span>
            {content.previewNote}
          </Text>
          <figcaption className="mt-4 border-t border-line pt-3 text-xs leading-4 text-subtle">
            Illustrative data. Your {content.name.toLowerCase()} is configured
            for your business.
          </figcaption>
        </CardContent>
      </Card>
    </figure>
  );
};
